import { type ColumnDef } from '@tanstack/react-table';
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react';

import { formatarData } from '@/lib/formato';
import { Carimbo } from '@/ui/components/carimbo';
import { DicaAjuda } from '@/ui/components/dica-ajuda';
import { Dinheiro } from '@/ui/components/dinheiro';
import { DocumentoTexto } from '@/ui/components/documento-texto';
import { type MetaColuna } from '@/ui/components/tabela-dados';
import { type DocumentoResumo } from '@fiscoai/contracts';

import { ClassificacaoDaNotaCelula } from './classificacao-da-nota';
import { IndicadorFiscal } from './indicador-fiscal';
import {
  AJUDA_DIRECAO,
  AJUDA_TIPO_OPERACAO,
  DIRECAO,
  MODELO,
  SITUACAO,
  TIPO_OPERACAO,
} from './rotulos';

const meta = (m: MetaColuna) => m;

export const colunasDocumentos: ColumnDef<DocumentoResumo>[] = [
  {
    id: 'emissao',
    header: 'Emissão',
    meta: meta({ largura: '7rem', ordenavel: 'emitidaEm' }),
    cell: ({ row }) => (
      <span className="tabular text-texto-suave">{formatarData(row.original.emitidaEm)}</span>
    ),
  },
  {
    id: 'numero',
    header: 'Nota',
    meta: meta({ largura: '9rem' }),
    cell: ({ row }) => (
      <span className="grid leading-tight">
        <span className="tabular font-medium">{Number(row.original.numero)}</span>
        <span className="text-xs text-texto-fraco">
          {MODELO[row.original.modelo]} série {Number(row.original.serie)}
        </span>
      </span>
    ),
  },
  {
    id: 'contraparte',
    header: 'Emitente / destinatário',
    cell: ({ row }) => (
      <span className="grid min-w-0 leading-tight">
        <span className="truncate font-medium">{row.original.contraparte.nome ?? 'Sem nome'}</span>
        <DocumentoTexto
          valor={row.original.contraparte.documento}
          className="text-xs text-texto-fraco"
        />
      </span>
    ),
  },
  {
    id: 'direcao',
    header: () => (
      <span className="inline-flex items-center gap-1">
        Direção
        <DicaAjuda rotulo="Sobre a coluna Direção" texto={AJUDA_DIRECAO} />
      </span>
    ),
    meta: meta({ largura: '7rem' }),
    cell: ({ row }) => {
      const entrada = row.original.direcao === 'ENTRADA';
      const Icone = entrada ? ArrowDownLeft : ArrowUpRight;
      return (
        <span
          className={`inline-flex items-center gap-1 text-sm ${entrada ? 'text-info' : 'text-texto-suave'}`}
        >
          <Icone className="size-3.5" aria-hidden />
          {DIRECAO[row.original.direcao]}
        </span>
      );
    },
  },
  {
    id: 'tipoOperacao',
    header: () => (
      <span className="inline-flex items-center gap-1">
        Tipo de operação
        <DicaAjuda rotulo="Sobre a coluna Tipo de operação" texto={AJUDA_TIPO_OPERACAO} />
      </span>
    ),
    meta: meta({ largura: '9.5rem' }),
    cell: ({ row }) => (
      <span className="text-sm text-texto-suave">
        {TIPO_OPERACAO[row.original.tipoOperacaoEmitente]}
      </span>
    ),
  },
  {
    id: 'situacao',
    header: 'Situação',
    meta: meta({ largura: '8.5rem' }),
    cell: ({ row }) => {
      const s = SITUACAO[row.original.situacao];
      return <Carimbo tom={s.tom}>{s.rotulo}</Carimbo>;
    },
  },
  {
    id: 'fiscal',
    header: 'Conferência',
    meta: meta({ largura: '9.5rem' }),
    cell: ({ row }) => <IndicadorFiscal doc={row.original} />,
  },
  {
    id: 'classificacao',
    header: 'Classificação',
    meta: meta({ largura: '10rem' }),
    cell: ({ row }) => (
      <ClassificacaoDaNotaCelula
        classificacao={row.original.classificacao}
        numero={row.original.numero}
      />
    ),
  },
  {
    id: 'valor',
    header: 'Valor',
    meta: meta({ alinhar: 'direita', largura: '9rem', ordenavel: 'valorTotal' }),
    cell: ({ row }) => <Dinheiro valor={row.original.valorTotal} className="font-medium" />,
  },
];
